import React, { useState, useEffect, useMemo } from "react";
import { Modal, Select, Input, Divider } from "antd";
import { FilterOutlined, PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";

/** Match dashboard item-list FilterPanel accents */
const FILTER_MODAL_THEME = {
    primary: "#314F71",
    editRowBg: "#f0f4ff",
    editRowBorder: "#314F71",
};

const OPERATOR_SHORT = {
    gt: ">",
    lt: "<",
    gte: "≥",
    lte: "≤",
    eq: "=",
    contains: "contains",
};

const EXPENSE_TYPE_FILTER_OPTIONS = [
    { value: "Once", label: "Once" },
    { value: "Daily", label: "Daily" },
    { value: "Weekly", label: "Weekly" },
    { value: "Monthly", label: "Monthly" },
];

const AD_COST_OPTIONS = [
    { value: "1", label: "Yes" },
    { value: "0", label: "No" },
];

/** Columns available for expense list API filters (same semantics as Products column filters) */
const EXPENSE_FILTER_COLUMNS = [
    { key: "type", label: "Type", type: "dropdown" },
    { key: "name", label: "Name", type: "contains_text" },
    { key: "category", label: "Category", type: "dropdown" },
    { key: "show_under_advertising_cost", label: "Advertising Cost", type: "dropdown" },
    { key: "products", label: "Product", type: "product" },
];

function displayFilterChipValue(filter) {
    if (filter.column === "show_under_advertising_cost") {
        return filter.value === "1" || filter.value === 1 ? "Yes" : "No";
    }
    return String(filter.value ?? "");
}

/**
 * Modal UX aligned with dashboard `FilterPanel` (Products / Order Items):
 * applied filter chips, add column → condition → value, edit/remove per row.
 */
export default function ExpenseFilterModal({
    open,
    onClose,
    activeFilters,
    addFilter,
    updateFilter,
    removeFilter,
    clearFilters,
    categoryFilterOptions,
    productFilterOptions,
    productFilterLoading,
    searchProductsForFilter,
}) {
    const [newCol, setNewCol] = useState(null);
    const [newOp, setNewOp] = useState(null);
    const [newVal, setNewVal] = useState("");

    useEffect(() => {
        if (!open) {
            setNewCol(null);
            setNewOp(null);
            setNewVal("");
        }
    }, [open]);

    const colDef = EXPENSE_FILTER_COLUMNS.find((c) => c.key === newCol);
    const existingFilter = activeFilters.find((f) => f.column === newCol);
    const isEditMode = !!existingFilter;
    const effectiveType = colDef?.type;
    const effectiveLabel = colDef?.label || newCol;

    const dropdownValueOptions = useMemo(() => {
        if (!newCol) return [];
        if (newCol === "type") return EXPENSE_TYPE_FILTER_OPTIONS;
        if (newCol === "category") return categoryFilterOptions || [];
        if (newCol === "show_under_advertising_cost") return AD_COST_OPTIONS;
        return [];
    }, [newCol, categoryFilterOptions]);

    const usedColumnKeys = useMemo(() => new Set(activeFilters.map((f) => f.column)), [activeFilters]);

    const handleColChange = (val) => {
        const existing = activeFilters.find((f) => f.column === val);
        const def = EXPENSE_FILTER_COLUMNS.find((c) => c.key === val);
        setNewCol(val);
        if (existing) {
            setNewOp(def?.type === "contains_text" ? "contains" : existing.operator);
            setNewVal(String(existing.value ?? ""));
        } else {
            if (def?.type === "dropdown" || def?.type === "product") setNewOp("eq");
            else if (def?.type === "contains_text") setNewOp("contains");
            else setNewOp(null);
            setNewVal("");
        }
    };

    const handleSubmit = () => {
        if (!newCol || !newOp) return;
        if (newVal === "" || newVal === null || newVal === undefined) return;

        const value =
            effectiveType === "dropdown" || effectiveType === "product"
                ? newVal
                : effectiveType === "contains_text"
                    ? String(newVal).trim()
                    : String(newVal).trim();
        if (value === "") return;

        if (isEditMode) {
            updateFilter(existingFilter.id, { operator: newOp, value });
        } else {
            addFilter({
                id: `${newCol}_${Date.now()}`,
                column: newCol,
                columnLabel: effectiveLabel,
                type: effectiveType,
                operator: newOp,
                value,
            });
        }
        setNewCol(null);
        setNewOp(null);
        setNewVal("");
    };

    const canSubmit =
        newCol &&
        newOp &&
        newVal !== "" &&
        newVal !== null &&
        newVal !== undefined &&
        String(newVal).trim() !== "";

    return (
        <Modal
            open={open}
            onCancel={onClose}
            title={
                <div className="d-flex align-items-center gap-2">
                    <FilterOutlined style={{ color: "#dc3545" }} />
                    <span className="fw-bold">Column Filters</span>
                    {activeFilters.length > 0 && (
                        <span className="badge bg-danger ms-1 text-light" style={{ fontSize: 11 }}>
                            {activeFilters.length} Active
                        </span>
                    )}
                </div>
            }
            footer={
                <div className="d-flex align-items-center justify-content-between w-100">
                    <span style={{ fontSize: 11, color: "#aaa" }} />
                    <div className="d-flex gap-2">
                        {activeFilters.length > 0 && (
                            <button type="button" className="btn btn-sm btn-outline-danger" onClick={clearFilters}>
                                Clear all filters
                            </button>
                        )}
                        <button type="button" className="btn btn-sm btn-danger" onClick={onClose}>
                            Done
                        </button>
                    </div>
                </div>
            }
            width={560}
            destroyOnClose={false}
            styles={{ body: { padding: "8px 0", maxHeight: "70vh", overflowY: "auto" } }}
        >
            {activeFilters.length > 0 && (
                <div style={{ padding: "0 24px 16px" }}>
                    <div
                        style={{
                            fontSize: 11,
                            fontWeight: 600,
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                            color: "#999",
                            marginBottom: 10,
                        }}
                    >
                        Applied Filters
                    </div>
                    <div className="d-flex flex-column gap-2">
                        {activeFilters.map((f) => {
                            const isBeingEdited = newCol === f.column;
                            return (
                                <div
                                    key={f.id}
                                    className="d-flex align-items-center justify-content-between rounded-2 px-3 py-2"
                                    style={{
                                        background: isBeingEdited ? FILTER_MODAL_THEME.editRowBg : "#fff4f4",
                                        border: isBeingEdited
                                            ? `1.5px solid ${FILTER_MODAL_THEME.editRowBorder}`
                                            : "1px solid #ffd6d6",
                                        transition: "background 0.2s, border-color 0.2s",
                                    }}
                                >
                                    <div className="d-flex align-items-center gap-2 flex-wrap">
                                        <span className="fw-semibold" style={{ fontSize: 13, color: "#1e1e2d" }}>
                                            {f.columnLabel}
                                        </span>
                                        <span
                                            style={{
                                                fontSize: 11,
                                                background: "#e9ecef",
                                                color: "#495057",
                                                padding: "2px 8px",
                                                borderRadius: 4,
                                                fontWeight: 600,
                                            }}
                                        >
                                            {OPERATOR_SHORT[f.operator] || f.operator}
                                        </span>
                                        <span className="fw-bold" style={{ fontSize: 13, color: "#dc3545" }}>
                                            {displayFilterChipValue(f)}
                                        </span>
                                        {isBeingEdited && (
                                            <span
                                                style={{
                                                    fontSize: 10,
                                                    background: FILTER_MODAL_THEME.primary,
                                                    color: "#fff",
                                                    padding: "2px 8px",
                                                    borderRadius: 4,
                                                    fontWeight: 600,
                                                    letterSpacing: "0.02em",
                                                }}
                                            >
                                                editing
                                            </span>
                                        )}
                                    </div>
                                    <div className="d-flex align-items-center gap-2">
                                        <button
                                            type="button"
                                            className="btn p-0 border-0 bg-transparent"
                                            style={{
                                                color: isBeingEdited ? FILTER_MODAL_THEME.primary : "#adb5bd",
                                                lineHeight: 1,
                                                fontSize: 15,
                                            }}
                                            onClick={() => handleColChange(f.column)}
                                            title="Edit this filter"
                                        >
                                            <EditOutlined />
                                        </button>
                                        <button
                                            type="button"
                                            className="btn p-0"
                                            style={{ color: "#ccc", lineHeight: 1 }}
                                            onClick={() => {
                                                removeFilter(f.id);
                                                if (isBeingEdited) {
                                                    setNewCol(null);
                                                    setNewOp(null);
                                                    setNewVal("");
                                                }
                                            }}
                                            title="Remove this filter"
                                        >
                                            <DeleteOutlined style={{ fontSize: 14 }} />
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <Divider style={{ margin: "16px 0 0" }} />
                </div>
            )}

            <div style={{ padding: "16px 24px 8px" }}>
                <div
                    style={{
                        fontSize: 11,
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        marginBottom: 14,
                        color: isEditMode ? FILTER_MODAL_THEME.primary : "#999",
                    }}
                >
                    {isEditMode ? (
                        <span className="d-inline-flex align-items-center gap-2">
                            <EditOutlined style={{ fontSize: 13 }} />
                            <span>Editing filter: {effectiveLabel}</span>
                        </span>
                    ) : activeFilters.length > 0 ? (
                        "Add Another Filter"
                    ) : (
                        "Add Filter"
                    )}
                </div>

                <div className="mb-3">
                    <div style={{ fontSize: 12, fontWeight: 500, color: "#555", marginBottom: 6 }}>1. Select Column</div>
                    <Select
                        placeholder="Choose a column to filter..."
                        style={{ width: "100%" }}
                        size="large"
                        value={newCol}
                        onChange={handleColChange}
                        showSearch
                        filterOption={(input, option) =>
                            String(option?.label ?? "")
                                .toLowerCase()
                                .includes(String(input).toLowerCase())
                        }
                        options={EXPENSE_FILTER_COLUMNS.map((c) => ({ value: c.key, label: c.label }))}
                        optionRender={(option) => (
                            <div className="d-flex align-items-center justify-content-between">
                                <span>{option.label}</span>
                                {usedColumnKeys.has(option.value) && (
                                    <span
                                        style={{
                                            fontSize: 10,
                                            background: option.value === newCol ? FILTER_MODAL_THEME.primary : "#e9ecef",
                                            color: option.value === newCol ? "#fff" : "#888",
                                            padding: "2px 7px",
                                            borderRadius: 4,
                                            fontWeight: 600,
                                        }}
                                    >
                                        {option.value === newCol ? "editing" : "filtered"}
                                    </span>
                                )}
                            </div>
                        )}
                    />
                </div>

                {newCol && (newOp || effectiveType === "dropdown" || effectiveType === "product" || effectiveType === "contains_text") && (
                    <div className="mb-4">
                        <div style={{ fontSize: 12, fontWeight: 500, color: "#555", marginBottom: 6 }}>
                            {effectiveType === "dropdown" || effectiveType === "product"
                                ? "2. Select Value"
                                : effectiveType === "contains_text"
                                    ? "2. Enter text (contains)"
                                    : "3. Enter Value"}
                        </div>
                        {effectiveType === "dropdown" ? (
                            <Select
                                placeholder="Select a value..."
                                style={{ width: "100%" }}
                                size="large"
                                value={newVal === "" ? undefined : newVal}
                                onChange={(val) => setNewVal(val ?? "")}
                                options={dropdownValueOptions}
                                notFoundContent={
                                    dropdownValueOptions.length === 0 ? (
                                        <span style={{ color: "#aaa", fontSize: 12 }}>No options available</span>
                                    ) : null
                                }
                            />
                        ) : effectiveType === "product" ? (
                            <Select
                                allowClear
                                showSearch
                                filterOption={false}
                                placeholder="Search and select a product (ASIN)"
                                style={{ width: "100%" }}
                                size="large"
                                value={newVal === "" ? undefined : newVal}
                                loading={productFilterLoading}
                                options={productFilterOptions}
                                optionFilterProp="label"
                                onSearch={searchProductsForFilter}
                                onDropdownVisibleChange={(o) => {
                                    if (o) searchProductsForFilter("");
                                }}
                                onChange={(val) => setNewVal(val ?? "")}
                            />
                        ) : (
                            <Input
                                placeholder={
                                    effectiveType === "contains_text"
                                        ? "Text to search for in expense name…"
                                        : "Enter text..."
                                }
                                size="large"
                                value={newVal}
                                onChange={(e) => setNewVal(e.target.value)}
                                onPressEnter={() => {
                                    if (canSubmit) handleSubmit();
                                }}
                            />
                        )}
                    </div>
                )}

                <button
                    type="button"
                    className={`fs-7 btn fw-bold w-100 d-flex align-items-center justify-content-center gap-2 ${isEditMode ? "" : "btn-danger"}`}
                    onClick={handleSubmit}
                    disabled={!canSubmit}
                    style={{
                        padding: "9px 0",
                        opacity: canSubmit ? 1 : 0.45,
                        ...(isEditMode
                            ? {
                                  background: FILTER_MODAL_THEME.primary,
                                  border: `1px solid ${FILTER_MODAL_THEME.primary}`,
                                  color: "#fff",
                              }
                            : {}),
                    }}
                >
                    {isEditMode ? (
                        <>
                            <EditOutlined style={{ fontSize: 14 }} /> Update Filter
                        </>
                    ) : (
                        <>
                            <PlusOutlined /> Add Filter
                        </>
                    )}
                </button>

                {isEditMode && (
                    <button
                        type="button"
                        className="btn btn-sm btn-link w-100 mt-2 border-0"
                        style={{ fontSize: 12, color: FILTER_MODAL_THEME.primary, opacity: 0.85 }}
                        onClick={() => {
                            setNewCol(null);
                            setNewOp(null);
                            setNewVal("");
                        }}
                    >
                        Cancel edit
                    </button>
                )}
            </div>
        </Modal>
    );
}
